import { v4 as uuidv4 } from 'uuid'
import { SHOULD_AUTOBATCH } from '@reduxjs/toolkit'
import { Action, ActionRepositoryMeta, ActionResponseMeta, getCurrentWindowId, ResponseType } from 'utils'
import {
  ClientMessageAdminLogsOutUserPayload,
  ClientMessageEventPayload,
  ClientMessageEventPayloadUnion,
  ClientMessageLoginPayload,
  ClientMessageLogPayload,
  ClientMessagePlatformChangePayload,
  ClientMessageTogglePlatformFeaturePayload,
  ClientMessageType as ClientActionTypes,
  CrudEvent,
  CrudEventUnion,
  DevSignal,
  ReportType,
  SubscribeMessage,
  UnsubscribeMessage
} from './generated'
import {
  ClientMessageUpdateLogLevelsPayload,
  DatabaseReportRequest,
  PermissionReportRequest,
  SubscribeBatchMessage,
  SubscriptionFilter,
  SubscriptionIdentifier,
  UpdateCollateralsClientRequest,
  UpdateIssuerOrganisationClientRequest
} from 'api'
import { getConfig } from 'configurationLoader'
import { Locale, Translation } from 'locale'

export type ClientSubscribeMessage = Omit<
  SubscribeMessage & {
    subscriptionIdentifier: SubscriptionIdentifier
  },
  'messageWhenStateCorrupt'
>

export type ClientSubscribeMessageWith = SubscribeMessage & {
  subscriptionIdentifier: SubscriptionIdentifier
}

export { ClientActionTypes }
export type ClientEventAction = Action<ClientActionTypes.EVENT_MESSAGE, ClientMessageEventPayload>
export type ClientSubscribeAction = Action<ClientActionTypes.SUBSCRIBE_MESSAGE, ClientSubscribeMessageWith>
export type ClientSubscribeBatchAction = Action<
  ClientActionTypes.SUBSCRIBE_BATCH_MESSAGE,
  SubscribeBatchMessage & {
    payload: ClientSubscribeMessage[]
  }
>
export type ClientUnsubscribeAction = Action<
  ClientActionTypes.UNSUBSCRIBE_MESSAGE,
  UnsubscribeMessage & {
    subscriptionIdentifier: SubscriptionIdentifier
  }
>
export type ClientDatabaseReportAction = Action<ClientActionTypes.DATABASE_REPORT_MESSAGE, DatabaseReportRequest>
export type ClientPermissionsReportAction = Action<ClientActionTypes.PERMISSIONS_REPORT, PermissionReportRequest>
export type ClientCrudEventAction = Action<ClientActionTypes.CRUD_EVENT_MESSAGE, CrudEvent>
export type ClientPlatformChangeAction = Action<ClientActionTypes.PLATFORM_CHANGE_MESSAGE, ClientMessagePlatformChangePayload>
export type ClientDevSignalAction = Action<ClientActionTypes.DEV_SIGNAL, DevSignal>
export type ClientLoginAction = Action<ClientActionTypes.LOGIN_MESSAGE, ClientMessageLoginPayload>
export type ClientLogToServerAction = Action<ClientActionTypes.LOG_MESSAGE, ClientMessageLogPayload>
export type ClientAdminLogsOutUserAction = Action<ClientActionTypes.ADMIN_LOGS_OUT_USER_MESSAGE, ClientMessageAdminLogsOutUserPayload>
export type ClientTogglePlatformFeatureAction = Action<
  ClientActionTypes.TOGGLE_PLATFORM_FEATURE_MESSAGE,
  ClientMessageTogglePlatformFeaturePayload
>
export type ClientUpdateLogLevelsAction = Action<ClientActionTypes.CLIENT_UPDATE_LOG_LEVELS_MESSAGE, ClientMessageUpdateLogLevelsPayload>
export type ClientUpdateCollateralsAction = Action<ClientActionTypes.UPDATE_COLLATERALS, UpdateCollateralsClientRequest>
export type ClientUpdateIssuerOrganisationAction = Action<
  ClientActionTypes.UPDATE_ISSUER_ORGANISATION,
  UpdateIssuerOrganisationClientRequest
>

export function clientActionEvent<TEvent extends ClientMessageEventPayloadUnion>(
  eventType: TEvent['eventType'],
  eventArgs: Omit<TEvent, 'eventType' | 'correlationId'>,
  sourceWindowId: string,
  repositoryMeta?: ActionRepositoryMeta,
  responseMeta?: ActionResponseMeta,
  correlationId?: string
): ClientEventAction {
  return {
    type: ClientActionTypes.EVENT_MESSAGE,
    payload: {
      eventType: eventType,
      correlationId: correlationId ?? uuidv4(),
      ...eventArgs
    },
    meta: { sendToServer: true, [SHOULD_AUTOBATCH]: true, sourceWindowId, responseMeta, repositoryMeta }
  }
}

export interface ClientActionSubscribeArgs extends Omit<ClientSubscribeMessage, 'correlationId'> {
  correlationId?: string
}

export type clientActionUpdateCollateralsArgs = Omit<UpdateCollateralsClientRequest, 'correlationId'>

export function clientActionUpdateCollaterals(eventArgs: clientActionUpdateCollateralsArgs): ClientUpdateCollateralsAction {
  return {
    type: ClientActionTypes.UPDATE_COLLATERALS,
    payload: { correlationId: uuidv4(), ...eventArgs },
    meta: {
      sendToServer: true,
      [SHOULD_AUTOBATCH]: true,
      sourceWindowId: getCurrentWindowId(),
      responseMeta: { successResponse: ResponseType.GROWL, errorResponse: ResponseType.GROWL }
    }
  }
}

export type clientActionUpdateIssuerOrganisationArgs = Omit<UpdateIssuerOrganisationClientRequest, 'correlationId'>

export function clientActionUpdateIssuerOrganisation(
  eventArgs: clientActionUpdateIssuerOrganisationArgs
): ClientUpdateIssuerOrganisationAction {
  return {
    type: ClientActionTypes.UPDATE_ISSUER_ORGANISATION,
    payload: { correlationId: uuidv4(), ...eventArgs },
    meta: {
      sendToServer: true,
      [SHOULD_AUTOBATCH]: true,
      sourceWindowId: getCurrentWindowId(),
      responseMeta: { successResponse: ResponseType.GROWL, errorResponse: ResponseType.GROWL }
    }
  }
}

export function clientActionSubscribe(eventArgs: ClientActionSubscribeArgs): ClientSubscribeAction {
  const messageWhenStateCorrupt = getConfig().messageWhenStateCorrupt ?? false

  return {
    type: ClientActionTypes.SUBSCRIBE_MESSAGE,
    payload: {
      correlationId: eventArgs.correlationId ?? uuidv4(),
      ...eventArgs,
      messageWhenStateCorrupt
    },
    meta: { sendToServer: true, [SHOULD_AUTOBATCH]: true, sourceWindowId: getCurrentWindowId() }
  }
}

export type ClientActionSubscribeBatchArgs = Omit<SubscribeBatchMessage, 'correlationId' | 'payload'> & {
  payload: ClientSubscribeMessage[]
}

export function clientActionSubscribeBatch(eventArgs: ClientActionSubscribeBatchArgs): ClientSubscribeBatchAction {
  const messageWhenStateCorrupt = getConfig().messageWhenStateCorrupt ?? false

  return {
    type: ClientActionTypes.SUBSCRIBE_BATCH_MESSAGE,
    payload: {
      correlationId: uuidv4(),
      ...eventArgs,
      payload: eventArgs.payload.map(m => ({ ...m, messageWhenStateCorrupt }))
    },
    meta: { sendToServer: true, [SHOULD_AUTOBATCH]: true, sourceWindowId: getCurrentWindowId() }
  }
}

export type ClientActionUnsubscribeArgs = Omit<UnsubscribeMessage, 'correlationId'> & {
  subscriptionIdentifier: SubscriptionIdentifier
}

export function clientActionUnsubscribe(eventArgs: ClientActionUnsubscribeArgs): ClientUnsubscribeAction {
  return {
    type: ClientActionTypes.UNSUBSCRIBE_MESSAGE,
    payload: {
      correlationId: uuidv4(),
      ...eventArgs
    },
    meta: { sendToServer: true, [SHOULD_AUTOBATCH]: true, sourceWindowId: getCurrentWindowId() }
  }
}

export function clientCrudActionEvent<TEvent extends CrudEventUnion>(
  eventType: TEvent['eventType'],
  eventArgs: Omit<TEvent, 'eventType' | 'correlationId'>,
  repositoryMeta?: ActionRepositoryMeta,
  responseMeta?: ActionResponseMeta
): ClientCrudEventAction {
  return {
    type: ClientActionTypes.CRUD_EVENT_MESSAGE,
    payload: {
      eventType: eventType,
      correlationId: uuidv4(),
      ...eventArgs
    },
    meta: { sendToServer: true, [SHOULD_AUTOBATCH]: true, sourceWindowId: getCurrentWindowId(), responseMeta, repositoryMeta }
  }
}

export type ClientActionPlatformChangeArgs = Omit<ClientMessagePlatformChangePayload, 'correlationId'>

export function clientActionPlatformChange(platformChangeArgs: ClientActionPlatformChangeArgs): ClientPlatformChangeAction {
  return {
    type: ClientActionTypes.PLATFORM_CHANGE_MESSAGE,
    payload: {
      ...platformChangeArgs,
      correlationId: uuidv4()
    },
    meta: {
      sendToServer: true,
      [SHOULD_AUTOBATCH]: true,
      sourceWindowId: getCurrentWindowId()
    }
  }
}

export function clientActionPermissionsReportRequest(organisationId: string): ClientPermissionsReportAction {
  return {
    type: ClientActionTypes.PERMISSIONS_REPORT,
    payload: {
      correlationId: uuidv4(),
      organisationId
    },
    meta: {
      sendToServer: true,
      [SHOULD_AUTOBATCH]: true,
      sourceWindowId: getCurrentWindowId(),
      responseMeta: {
        successMessage: Locale.translate(Translation.SC_PERMISSIONS_REPORT_SUCCESS),
        successResponse: ResponseType.GROWL,
        errorResponse: ResponseType.GROWL
      }
    }
  }
}

export function clientActionDataBaseReportRequest(subscriptionFilters: SubscriptionFilter[]): ClientDatabaseReportAction {
  return {
    type: ClientActionTypes.DATABASE_REPORT_MESSAGE,
    payload: {
      filters: subscriptionFilters,
      reportType: ReportType.RECONCILIATION,
      correlationId: uuidv4()
    },
    meta: {
      sendToServer: true,
      [SHOULD_AUTOBATCH]: true,
      sourceWindowId: getCurrentWindowId(),
      responseMeta: {
        successMessage: Locale.translate(Translation.SC_EXTRACT_REPORT_SUCCESS),
        successResponse: ResponseType.GROWL,
        errorResponse: ResponseType.GROWL
      }
    }
  }
}

export type ClientActionDevSignalArgs = Omit<DevSignal, 'correlationId'>

export function clientActionDevSignal(devSignalArgs: ClientActionDevSignalArgs): ClientDevSignalAction {
  return {
    type: ClientActionTypes.DEV_SIGNAL,
    payload: {
      ...devSignalArgs,
      correlationId: uuidv4()
    },
    meta: {
      sendToServer: true,
      [SHOULD_AUTOBATCH]: true,
      sourceWindowId: getCurrentWindowId()
    }
  }
}

export function clientActionLogin(loginArgs: Omit<ClientMessageLoginPayload, 'correlationId'>): ClientLoginAction {
  return {
    type: ClientActionTypes.LOGIN_MESSAGE,
    payload: {
      ...loginArgs,
      correlationId: uuidv4()
    },
    meta: {
      sendToServer: true,
      sourceWindowId: getCurrentWindowId(),
      responseMeta: {
        errorResponse: ResponseType.NONE
      }
    }
  }
}

export type ClientActionLogToServerArgs = Omit<ClientMessageLogPayload, 'correlationId'>

export function clientActionLogToServer(logErrorArgs: ClientActionLogToServerArgs, debounce?: boolean): ClientLogToServerAction {
  return {
    type: ClientActionTypes.LOG_MESSAGE,
    payload: {
      ...logErrorArgs,
      correlationId: uuidv4()
    },
    meta: {
      sendToServer: true,
      [SHOULD_AUTOBATCH]: debounce ?? false,
      sourceWindowId: getCurrentWindowId(),
      responseMeta: {
        errorResponse: ResponseType.NONE
      }
    }
  }
}

export type ClientActionAdminLogsOutUserArgs = Omit<ClientMessageAdminLogsOutUserPayload, 'correlationId'>

export function clientActionAdminLogsOutUser(usernameBeingLoggedOut: ClientActionAdminLogsOutUserArgs): ClientAdminLogsOutUserAction {
  return {
    type: ClientActionTypes.ADMIN_LOGS_OUT_USER_MESSAGE,
    payload: {
      ...usernameBeingLoggedOut,
      correlationId: uuidv4()
    },
    meta: {
      sendToServer: true,
      sourceWindowId: getCurrentWindowId()
    }
  }
}

export type ClientActionTogglePlatformFeatureArgs = Omit<ClientMessageTogglePlatformFeaturePayload, 'correlationId'>

export function clientActionTogglePlatformFeature({
  platformFeature,
  enabled
}: ClientActionTogglePlatformFeatureArgs): ClientTogglePlatformFeatureAction {
  return {
    type: ClientActionTypes.TOGGLE_PLATFORM_FEATURE_MESSAGE,
    payload: {
      platformFeature,
      enabled,
      correlationId: uuidv4()
    },
    meta: {
      sendToServer: true,
      sourceWindowId: getCurrentWindowId()
    }
  }
}

export function clientActionUpdateLogLevels(
  config: ClientMessageUpdateLogLevelsPayload['clientLogConfig']['config']
): ClientUpdateLogLevelsAction {
  return {
    type: ClientActionTypes.CLIENT_UPDATE_LOG_LEVELS_MESSAGE,
    payload: {
      clientLogConfig: {
        config
      },
      correlationId: uuidv4()
    },
    meta: {
      sendToServer: true,
      sourceWindowId: getCurrentWindowId()
    }
  }
}
